import { useState, useEffect } from 'react'
import { getIdeas, createIdea, updateIdea } from '../services/ideas'
import { ApiErrorException } from '../services/client'
import type { Idea } from '../services/types'
import Modal from '../components/Modal'
import IdeaForm, { type FormData } from '../components/IdeaForm'
import { useToast } from '../components/Toast'
import GraphView3D from './GraphView3D'

function GraphView() {
  const [ideas, setIdeas] = useState<Idea[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingIdea, setEditingIdea] = useState<Idea | null>(null)
  const { showSuccess, showError } = useToast()

  useEffect(() => {
    loadIdeas()
  }, [])

  const loadIdeas = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getIdeas()
      setIdeas(response.items)
    } catch (err) {
      console.error('Failed to load ideas:', err)
      const message = err instanceof ApiErrorException
        ? err.error
        : 'Connection failed. Check network status.'
      setError(message)
      showError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateIdea = async (data: FormData) => {
    try {
      const payload: any = {
        name: data.name,
        rank: data.rank,
      }
      if (data.description) payload.description = data.description
      if (data.category) payload.category = data.category

      await createIdea(payload)
      setIsModalOpen(false)
      showSuccess('Idea initialized successfully')
      loadIdeas()
    } catch (err) {
      const message = err instanceof ApiErrorException
        ? err.error
        : 'Initialization failed. Retry operation.'
      showError(message)
      throw err
    }
  }

  const handleUpdateIdea = async (data: FormData) => {
    if (!editingIdea) return
    try {
      const payload: any = {
        name: data.name,
        rank: data.rank,
        status: data.status,
      }
      if (data.description) payload.description = data.description
      if (data.category) payload.category = data.category

      await updateIdea(editingIdea.id, payload)
      setEditingIdea(null)
      showSuccess('Idea updated successfully')
      loadIdeas()
    } catch (err) {
      const message = err instanceof ApiErrorException
        ? err.error
        : 'Update failed. Retry operation.'
      showError(message)
      throw err
    }
  }

  const handleIdeaClick = (idea: Idea) => {
    setEditingIdea(idea)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingIdea(null)
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--glass-border)]">
        <div>
          <h1 className="text-2xl font-bold tracking-wider mb-1">Idea Space</h1>
          <p className="text-[var(--chrome-300)] text-sm">
            {ideas.length} {ideas.length === 1 ? 'node' : 'nodes'} • 3D Semantic Visualization
          </p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="btn-primary flex items-center gap-2"
        >
          <span className="text-lg leading-none">+</span>
          <span>New Idea</span>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 relative">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="loading-spinner mb-4" />
            <p className="text-[var(--chrome-300)] text-sm tracking-wider uppercase">
              Loading idea space...
            </p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="glass-panel p-8 text-center">
              <div className="w-12 h-12 mx-auto mb-4 border-2 border-[var(--alert-coral)] rotate-45 flex items-center justify-center">
                <span className="text-[var(--alert-coral)] text-xl -rotate-45">!</span>
              </div>
              <p className="text-[var(--alert-coral)] mb-4">{error}</p>
              <button onClick={loadIdeas} className="btn-ghost">
                Retry Connection
              </button>
            </div>
          </div>
        ) : ideas.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="glass-panel p-12 text-center">
              <div className="relative w-20 h-20 mx-auto mb-6">
                <div className="absolute inset-0 border-2 border-[var(--chrome-400)] rotate-45 opacity-30" />
                <div className="absolute inset-4 border border-[var(--chrome-400)] rotate-45 opacity-20" />
              </div>
              <h3 className="text-xl text-[var(--chrome-200)] mb-2">No Ideas Found</h3>
              <p className="text-[var(--chrome-400)] text-sm mb-6">
                Initialize your first idea to begin building the ideaverse
              </p>
              <button onClick={() => setIsModalOpen(true)} className="btn-primary">
                Create First Idea
              </button>
            </div>
          </div>
        ) : (
          <GraphView3D ideas={ideas} onIdeaClick={handleIdeaClick} />
        )}
      </div>

      {/* Modals */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Initialize New Idea"
      >
        <IdeaForm
          mode="create"
          onSubmit={handleCreateIdea}
          onCancel={handleCloseModal}
        />
      </Modal>

      <Modal
        isOpen={!!editingIdea}
        onClose={handleCloseModal}
        title="Modify Idea Node"
      >
        {editingIdea && (
          <IdeaForm
            mode="edit"
            initialData={editingIdea}
            onSubmit={handleUpdateIdea}
            onCancel={handleCloseModal}
          />
        )}
      </Modal>
    </div>
  )
}

export default GraphView
